import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, useLocation } from 'react-router-dom';
import { matchRoutes, useNavigationType } from 'react-router';

const SENTRY_DSN = 'https://6571b5ae5b5b4a12a9d7c803bc405a44@o158945.ingest.sentry.io/4505550777679872';

const SENSITIVE_PARAM_RE = /([?&])(auth_code|access_token|refresh_token|id_token|token|code|jwt)=[^&#]*/gi;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._\-+/=]+/g;

const scrubUrlString = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(SENSITIVE_PARAM_RE, '$1$2=[REDACTED]').replace(BEARER_RE, 'Bearer [REDACTED]');
};

const scrubHeaders = (headers) => {
  if (!headers || typeof headers !== 'object') return headers;
  const out = Array.isArray(headers) ? [...headers] : { ...headers };
  if (Array.isArray(out)) {
    return out.map(([k, v]) => [k, k && k.toLowerCase() === 'authorization' ? '[REDACTED]' : scrubUrlString(v)]);
  }
  Object.keys(out).forEach((k) => {
    if (k.toLowerCase() === 'authorization') {
      out[k] = '[REDACTED]';
    } else if (typeof out[k] === 'string') {
      out[k] = scrubUrlString(out[k]);
    }
  });
  return out;
};

const isLocalMode = ({ environment = process.env.NODE_ENV, host = window.location.host } = {}) => {
  return environment === 'development' || host.includes('localhost');
};

export const initializeSentry = (config = {}) => {
  if (isLocalMode(config)) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: false,
    integrations: [
      new Sentry.BrowserTracing({
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      new Sentry.Replay({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/tornado\.com\/api/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        if (typeof breadcrumb.data.url === 'string') {
          breadcrumb.data.url = scrubUrlString(breadcrumb.data.url);
        }
        if (typeof breadcrumb.data.to === 'string') {
          breadcrumb.data.to = scrubUrlString(breadcrumb.data.to);
        }
        if (typeof breadcrumb.data.from === 'string') {
          breadcrumb.data.from = scrubUrlString(breadcrumb.data.from);
        }
      }
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = scrubUrlString(breadcrumb.message);
      }
      return breadcrumb;
    },

    beforeSend(event) {
      if (event.request) {
        if (typeof event.request.url === 'string') {
          event.request.url = scrubUrlString(event.request.url);
        }
        if (event.request.headers) {
          event.request.headers = scrubHeaders(event.request.headers);
        }
        if (typeof event.request.query_string === 'string') {
          event.request.query_string = scrubUrlString('?' + event.request.query_string).replace(/^\?/, '');
        }
      }
      if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs.map((b) => {
          if (b.data?.url) b.data.url = scrubUrlString(b.data.url);
          if (typeof b.message === 'string') b.message = scrubUrlString(b.message);
          return b;
        });
      }
      return event;
    },
  });
};
